import { configureStore, createSlice } from "@reduxjs/toolkit";
import { generateUniqueRuleID, id_seperator } from "@app/modules/RuleSets/utils";

const PersonaFormSlice = createSlice({
	name: "PersonaForm",
	initialState: {
		isEditing: false,
		isValid: false,
		loading: false,
		error: "",
		id: null, // Used only on Edit page.
		title: "",
		checkingLogic: "OR",
		ruleSets: [],
		isRevision: false,
	},
	reducers: {
		/**
		 * Set ID state.
		 *
		 * @param state
		 * @param action
		 */
		setID(state, action) {
			state.id = action.payload;
		},
		/**
		 * Set title state.
		 *
		 * @param state
		 * @param action
		 */
		setTitle(state, action) {
			state.title = action.payload;
			PersonaFormSlice.caseReducers.validateRules(state);
		},

		/**
		 * Set checking logic state.
		 *
		 * @param state
		 * @param action
		 */
		setCheckingLogic(state, action) {
			state.checkingLogic = action.payload;
		},

		/**
		 * Set isEditing state.
		 *
		 * @param state
		 * @param action
		 */
		setIsEditing(state, action) {
			state.isEditing = action.payload;
		},

		/**
		 * Set loading state.
		 *
		 * @param state
		 * @param action
		 */
		setLoading(state, action) {
			state.loading = action.payload;
		},

		/**
		 * Set error state.
		 *
		 * @param state
		 * @param action
		 */
		setError(state, action) {
			state.error = action.payload;
		},

		/**
		 * Add new rule set to the list.
		 *
		 * @param state The current state.
		 * @param action The rule object.
		 */
		addRuleSet(state, action) {
			let ruleSet = state.ruleSets.find(
				(rule) => rule.id === action.payload.id,
			);
			if (!ruleSet || true == action.payload.multi) {
				if(true == action.payload.multi){
					action.payload.id = generateUniqueRuleID(action.payload.id.split(id_seperator)[0], state.ruleSets);
				}
				state.ruleSets.push({...action.payload});
				PersonaFormSlice.caseReducers.validateRules(state);
			}
		},

		/**
		 * Add all rule sets to the list.
		 *
		 * @param state The current state.
		 * @param action The rule object.
		 */
		addRuleSets(state, action) {
			state.ruleSets = action.payload;
			PersonaFormSlice.caseReducers.validateRules(state);
		},

		/**
		 * Add is Revision.
		 *
		 * @param state The current state.
		 * @param action The boolean.
		 */
		setIsRevision(state, action) {
			state.isRevision = action.payload;
		},

		/**
		 * Update rule set in the list.
		 *
		 * @param state The current state.
		 * @param action The rule object.
		 */
		updateRuleSet(state, action) {
			let ruleSet = state.ruleSets.find(
				(rule) => rule.id === action.payload.id,
			);
			ruleSet = action.payload;
			PersonaFormSlice.caseReducers.validateRules(state);
		},

		/**
		 * search for rule inside ruleset by id
		 * update config of the searched rule
		 * and update the ruleset
		 */
		setRuleConfig(state, action) {
			let ruleSet = state.ruleSets.find(
				(rule) => rule.id === action.payload.id,
			);
			ruleSet.config = action.payload.config;
			PersonaFormSlice.caseReducers.validateRules(state);
		},

		/**
		 * Delete rule set from the list.
		 *
		 * @param state The current state.
		 * @param action
		 * @param action.payload The RuleSet ID.
		 */
		deleteRuleSet(state, action) {
			state.ruleSets = state.ruleSets.filter(
				(rule) => rule.id !== action.payload,
			);
		},

		/**
		 * validate the rulesets based on the rule config element if it has at least one non-empty item
		 * then set isValid to true otherwise false
		 */
		validateRules(state) {
			let isValid = true;
			let ruleSets = state.ruleSets.map((rule) => {
				rule.validationStatus = true;
				const inputs = Object.values(rule?.config?.args || {});
				if (
					inputs.includes(undefined) ||
					inputs.includes(null) ||
					inputs.includes("")
				) {
					isValid = false;
					rule.validationStatus = false;
				}
				return rule;
			});

			if (!state.title) {
				isValid = false;
			}

			state.isValid = isValid;
			state.ruleSets = ruleSets;
		},
	},
});

export const {
	addRuleSet,
	addRuleSets,
	setIsRevision,
	deleteRuleSet,
	setCheckingLogic,
	setError,
	setID,
	setIsEditing,
	setLoading,
	setRuleConfig,
	setTitle,
	updateRuleSet,
} = PersonaFormSlice.actions;

const store = configureStore({
	reducer: PersonaFormSlice.reducer,
});
export default store;

export function getSubmitData(store) {
	return {
		title: store.title,
		config: {
			checking_logic: store.checkingLogic,
			rules: store.ruleSets,
		},
	};
}

export function getUpdateData(store) {
	return {
		id: store.id,
		title: store.title,
		config: {
			checking_logic: store.checkingLogic,
			rules: store.ruleSets,
		},
	};
}
